import cn from 'classnames';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import React, {useEffect, useRef, useState} from 'react';

import {RootState} from '../../store';
import {getProjectData} from '../../store/project';
import {TaskInfo} from '../../types/metadata/TaskInfo';
import {toast} from '../../base-components/StudioToast';
import {useDeploymentDetails} from '../TestDeployView/useDeploy';
import {TestInferenceResults} from '../TestDeployView/TestInferenceResults';
import {
  EVENT_TYPE,
  VideoStreamEvent,
  VideoStreamSubject,
  VideoStreamObserver,
  DefaultMonitorConfig,
  VideoStreamController,
} from '../TestDeployView/HlsVideoStream';

import './OpenVXRuntimeStreamContainer.scss';

type OpenVXRuntimeStreamContainerProps = {
  projectId: string;
  deploymentId: string;
  spaId?: string;
  spaPostProcessorId?: string;
  displayRealtimeParameters: boolean;
  streamSubject: VideoStreamSubject;
  onFinish?: () => void;
  onRunning?: () => void;
  onError?: (message?: string) => void;
};

export const OpenVXRuntimeStreamContainer = ({
  spaId,
  projectId,
  deploymentId,
  streamSubject,
  spaPostProcessorId,
  displayRealtimeParameters,
  onError,
  onFinish,
  onRunning,
}: OpenVXRuntimeStreamContainerProps) => {
  const intl = useIntl();

  const deployment = useDeploymentDetails(deploymentId);
  const [videoController, setVideoController] = useState<VideoStreamController>();
  const projectData = useSelector(getProjectData);
  const serverConfig = useSelector((state: RootState) => state.config);

  const onErrorRef = useRef(onError);
  const onFinishRef = useRef(onFinish);
  const onRunningRef = useRef(onRunning);

  const startVideoStream = (controller: VideoStreamController, taskInfo?: TaskInfo) => {
    if (
      taskInfo?.videoOutputHlsUrl &&
      taskInfo.gspBoxOutputUrl &&
      taskInfo.deploymentId &&
      deployment?.deploymentInput?.inputSourceType
    ) {
      controller.start({
        limitedDuration: true,
        source: deployment.deploymentInput.inputSourceType,
        url: taskInfo.videoOutputHlsUrl,
        sourceUrl: taskInfo.gspBoxOutputUrl,
        callerSession: taskInfo.callerSession,
        targetSession: taskInfo.targetSession,
        streamModel: 'PULL',
        projectId,
        serverConfig,
        monitorConfig: {
          ...DefaultMonitorConfig,
          monitorStream: true,
          projectId,
          deploymentId: taskInfo.deploymentId,
        },
        streamSubject,
      });
    }
  };

  class OpenVXSpaStreamObserver extends VideoStreamObserver {
    controller: VideoStreamController;

    constructor(controller: VideoStreamController) {
      super();
      this.controller = controller;
    }

    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.READY_TO_START:
          onRunningRef.current?.();
          startVideoStream(this.controller, event.taskInfo);
          break;
        case EVENT_TYPE.ERROR:
          let errorMessage = intl.formatMessage({
            id: 'openVXRuntimeStream.error.defaultError',
          });

          if (event.message) {
            errorMessage = intl.formatMessage(
              {id: 'openVXRuntimeStream.error.customError'},
              {errorMessage: event.message}
            );
          }

          toast.error(errorMessage);
          onErrorRef.current?.(errorMessage);

          event.deploymentId = deploymentId;
          break;
        case EVENT_TYPE.STREAM_FINISHED:
          event.deploymentId = deploymentId;
          onFinishRef.current?.();
          break;
        default:
          break;
      }
    }
  }

  useEffect(() => {
    if (!deployment) {
      return;
    }
    const controller: VideoStreamController = new VideoStreamController();
    setVideoController(controller);
    streamSubject.addListener(
      'OpenVXSpaStreamObserver',
      new OpenVXSpaStreamObserver(controller)
    );

    const cleanup = () => {
      streamSubject.raiseStreamFinishEvent();
    };
    window.addEventListener('beforeunload', cleanup);

    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId, deployment]);

  return (
    <div className="test-results">
      <div
        className={cn(
          'test-results__split-inference-results',
          'test-results__split-inference-results--streaming'
        )}
      >
        <TestInferenceResults
          deployment={deployment}
          hasTransformation={false}
          isStreamingInference={true}
          isOtherTaskInProgress={false}
          projectData={projectData}
          videoController={videoController}
          streamSubject={streamSubject}
          spaId={spaId}
          spaPostProcessorId={spaPostProcessorId}
          displayRealtimeParameters={displayRealtimeParameters}
        />
      </div>
    </div>
  );
};
